import { Input as CatalystInput } from "../Catalyst/input";
import { Textarea as CatalystTextarea } from "../Catalyst/textarea";
import { Field, Label, ErrorMessage, Description } from "../Catalyst/fieldset";
import { Button } from "../Catalyst/button";
import { DocumentIcon } from "@heroicons/react/24/outline";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/20/solid";
import RichTextEditor from "../../components/Editor/PageEditor";
import { ClipLoader } from "react-spinners";
import clsx from "clsx";

const Input = ({
  name,
  label,
  value,
  onChange,
  className = '',
  rows = 2,
  requiredFlag = false,
  type = "text",
  error = '',
  disabled = false,
  readyOnly = false,
  fileInputReference = null,
  placeholder = '',
  onBlur = undefined,
  description = /** @type {React.ReactNode} */ (null), // helper text (or node) rendered under the field as a Catalyst Description
  autoComplete = false,
  autoFocus = false,
  loading = false,
  status = null,          // null | 'checking' | 'success' | 'error' — right-side inline status indicator
  statusTooltip = '',     // hover text shown on the status indicator
  ignorePasswordManagers = false, // opt password managers (1Password/LastPass/etc.) out of this field
}) => {
  // Tell password managers / autofill to leave this field alone so their injected
  // icons don't sit on top of our own indicators. Use only on non-credential fields.
  const passwordManagerIgnoreProps = ignorePasswordManagers
    ? { 'data-1p-ignore': true, 'data-lpignore': 'true', 'data-bwignore': true, 'data-form-type': 'other' }
    : {};
  // Handle change event - convert to match expected format
  const handleChange = (e) => {
    if (onChange) {
      onChange(e);
    }
  };

  // Merge default w-full with any custom className
  const fieldClassName = clsx("w-full", className);

  return (
    <>
      {(type === "text" || type === "password") && (
        <Field disabled={disabled} className={fieldClassName}>
          {label && (
            <Label>
              {label}
              {requiredFlag && <span className="text-red-500 dark:text-red-400"> *</span>}
            </Label>
          )}
          <div className="relative">
            <CatalystInput
              name={name}
              type={type}
              value={value || ""}
              onChange={handleChange}
              placeholder={placeholder}
              onBlur={onBlur}
              autoComplete={autoComplete ? "on" : "off"}
              autoFocus={autoFocus}
              invalid={!!error || status === "error"}
              {...passwordManagerIgnoreProps}
            />
            {loading && (
              <div className="absolute right-3 top-1/2 -translate-y-[45%] z-50 pointer-events-none bg-surface">
                <ClipLoader color="#6366f1" size={18} />
              </div>
            )}
            {/* Inline status indicator (right, inside the field) — keeps the message in a
                tooltip so the surrounding layout doesn't shift when a result comes back. */}
            {/* right-1.5 matches the pill's gap to the field's top and bottom
                edges, so the inset reads even on all three sides. */}
            {status && (
              <div className="absolute right-1.5 top-1/2 -translate-y-1/2 z-10 flex items-center">
                <span
                  className={clsx(
                    "inline-flex items-center gap-1.5 rounded-md py-1 pl-1.5 pr-2.5 text-xs font-medium shadow-xs ring-1 ring-inset",
                    status === "success" && "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 ring-emerald-600/20",
                    status === "error" && "bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-400 ring-red-600/20",
                    status === "checking" && "bg-surface-muted text-foreground-secondary ring-border"
                  )}
                  title={statusTooltip || undefined}
                >
                  {status === "checking" && <ClipLoader color="#71717a" size={13} />}
                  {status === "success" && <CheckCircleIcon className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />}
                  {status === "error" && <XCircleIcon className="h-4 w-4 text-red-600 dark:text-red-400" />}
                  {statusTooltip && <span className="max-w-60 truncate">{statusTooltip}</span>}
                </span>
              </div>
            )}
          </div>
          {description && <Description className="text-sm">{description}</Description>}
          {error && <ErrorMessage>{error}</ErrorMessage>}
        </Field>
      )}
      {type === "textArea" && (
        <Field disabled={disabled} className={fieldClassName}>
          <Label>
            {label}
            {requiredFlag && <span className="text-red-500 dark:text-red-400"> *</span>}
          </Label>
          <CatalystTextarea
            name={name}
            value={value || ""}
            onChange={handleChange}
            placeholder={placeholder}
            onBlur={onBlur}
            rows={rows}
            resizable={true}
            invalid={!!error}
          />
          {description && <Description className="text-sm">{description}</Description>}
          {error && <ErrorMessage>{error}</ErrorMessage>}
        </Field>
      )}
      {type === "file" && (
        <div className={className}>
          <Button
            type="button"
            outline
            disabled={disabled}
            onClick={(e) => {
              e.preventDefault();
              fileInputReference.current.click();
            }}
          >
            <DocumentIcon className="h-4 w-4" />
            Choose {label} File
          </Button>
          <input
            ref={fileInputReference}
            type="file"
            hidden
            name={name}
            onChange={onChange}
          />
          {error && <p className="text-sm mt-1 text-text-error">{error}</p>}
        </div>
      )}
      {type === "richText" && (
        <Field disabled={disabled} className={fieldClassName}>
          <Label>
            {label}
            {requiredFlag && <span className="text-red-500 dark:text-red-400"> *</span>}
          </Label>
          <RichTextEditor
            id={name}
            name={name}
            readyOnly={readyOnly}
            type={type}
            value={value}
            onBlur={onBlur}
            onChange={onChange}
            placeholder={placeholder}
          />
          {error && <ErrorMessage>{error}</ErrorMessage>}
        </Field>
      )}
    </>
  );
};

export default Input;
